import { X, Calendar, Clock, MapPin, DollarSign, ExternalLink, Eye, Rocket, BarChart3, Globe } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button } from './ui/button';
import { formatPrice, getEventTime } from '../lib/helpers';
import { CATEGORY_LABELS } from '../constants/categories';

// Boost plan labels
const BOOST_PLAN_LABELS = {
  'basic': 'Basic (1 dia)',
  'silver': 'Silver (3 dias)',
  'gold': 'Gold (7 dias)',
  1: 'Basic (1 dia)',
  3: 'Silver (3 dias)',
  7: 'Gold (7 dias)'
};

export const EventPreview = ({ event, onClose, onBoost }) => {
  const formatarPeriodoEvento = (startDate, endDate) => {
  if (!startDate || !endDate) return '';

  const start = startDate.toDate ? startDate.toDate() : new Date(startDate);
  const end = endDate.toDate ? endDate.toDate() : new Date(endDate);

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  const formatShort = (d) =>
    `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;

  const isSameDay = startDay.getTime() === endDay.getTime();

  if (isSameDay) {
    return startDay.getTime() === today.getTime() ? 'Hoje' : formatShort(start);
  }

  if (startDay.getTime() === today.getTime()) {
    return `Hoje até ${formatShort(end)}`;
  }

  if (startDay.getTime() === tomorrow.getTime()) {
    return `Amanhã até ${formatShort(end)}`;
  }

  return `${formatShort(start)} até ${formatShort(end)}`;
};

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300';
      case 'rejected':
        return 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved':
        return 'Aprovado';
      case 'pending':
        return 'Pendente';
      case 'rejected':
        return 'Rejeitado';
      default:
        return status;
    }
  };

  const hasValidCoordinates = event.latitude !== 0 && event.longitude !== 0;
  
  // Check if event is boosted using correct fields: boosted === true AND boostEnd > now
  const isBoosted = event.boosted === true;
  const getBoostEndDate = () => {
    if (!event.boostEnd) return null;
    return event.boostEnd.toDate ? event.boostEnd.toDate() : new Date(event.boostEnd);
  };
  const boostEndDate = getBoostEndDate();
  const isBoostActive = isBoosted && boostEndDate && boostEndDate > new Date();
  
  const getBoostLabel = () => {
    if (!isBoostActive) return null;
    return BOOST_PLAN_LABELS[event.boostType] || 'Impulsionado';
  };
  
  const getBoostRemainingTime = () => {
    if (!boostEndDate || boostEndDate <= new Date()) return null;
    return formatDistanceToNow(boostEndDate, { locale: ptBR, addSuffix: true });
  };
  
  // Views: únicas (por usuário logado) e totais (bruto, sem dedupe)
  const viewsUnique = event.viewsUnique || 0;
  const viewsTotal = event.views || 0;
  // Views do site (página do evento em joynapp.com.br): por navegador e por sessão
  const webViewsUnique = event.webViewsUnique || 0;
  const webViewsTotal = event.webViews || 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-card border border-border rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="event-preview-modal"
      >
        {/* Header with image - clean, no text overlay on image itself */}
        {event.imageUrl && (
          <div className="relative h-64 overflow-hidden rounded-t-xl">
            <img 
              src={event.imageUrl} 
              alt={event.title}
              className="w-full h-full object-cover"
            />
            {/* Boost badge at bottom right */}
            {isBoostActive && (
              <div className="absolute bottom-4 right-4 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-xl flex items-center gap-2">
                <Rocket className="w-4 h-4" />
                Impulsionado
              </div>
            )}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onClose}
              className="absolute top-4 left-4 bg-white/90 hover:bg-white"
              data-testid="close-preview-btn"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        )}

        {!event.imageUrl && (
          <div className="flex justify-end p-4 border-b border-border">
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        )}

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Title and Status */}
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-4">
              <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {event.title}
              </h2>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(event.status)}`}>
                  {getStatusText(event.status)}
                </span>
                {onBoost && (
                  <Button
                    size="sm"
                    className="gap-1.5 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white border-0"
                    onClick={() => onBoost(event)}
                  >
                    <Rocket className="w-3.5 h-3.5" />
                    Impulsionar
                  </Button>
                )}
              </div>
            </div>

            <span className="inline-block px-3 py-1 bg-secondary rounded-full text-sm font-medium">
              {CATEGORY_LABELS[event.category] || event.category}
            </span>
          </div>

          {/* Boost status banner */}
          {isBoostActive && (
            <div className="bg-gradient-to-r from-orange-500/10 to-pink-500/10 border border-orange-500/30 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-r from-orange-500 to-pink-500 p-2 rounded-lg">
                    <Rocket className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-orange-700 dark:text-orange-300">Evento Impulsionado</p>
                    <p className="text-sm text-muted-foreground">Plano: {getBoostLabel()}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Expira</p>
                  <p className="font-medium text-orange-700 dark:text-orange-300">{getBoostRemainingTime()}</p>
                </div>
              </div>
            </div>
          )}

          {/* Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Date and Time */}
            <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
              <Calendar className="w-5 h-5 mt-0.5 text-primary" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Data e Hora</p>
                <p className="font-semibold capitalize">{formatarPeriodoEvento(event.startDate, event.endDate)}</p>
                <p className="text-sm">{getEventTime(event)}</p>
              </div>
            </div>

            {/* Location */}
            <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
              <MapPin className="w-5 h-5 mt-0.5 text-primary" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Localização</p>
                <p className="font-semibold">{event.city}</p>
                <p className="text-sm">{event.location}</p>
              </div>
            </div>

            {/* Price */}
            <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
              <DollarSign className="w-5 h-5 mt-0.5 text-primary" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Ingresso</p>
                {event.isFree ? (
                  <p className="font-semibold text-lg text-green-600 dark:text-green-400">Gratuito</p>
                ) : (
                  <p className="font-semibold text-lg">{formatPrice(event.ticketPrice)}</p>
                )}
                {!event.isFree && event.ticketUrl && (
                  <a 
                    href={event.ticketUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline inline-flex items-center gap-1 mt-1"
                  >
                    Comprar ingresso
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>

            {/* Views - Unique */}
            <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
              <Eye className="w-5 h-5 mt-0.5 text-primary" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Visualizações únicas</p>
                <p className="font-semibold text-lg">{viewsUnique}</p>
                <p className="text-xs text-muted-foreground">Por usuário logado, 1x cada</p>
              </div>
            </div>

            {/* Views - Total */}
            <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
              <BarChart3 className="w-5 h-5 mt-0.5 text-primary" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Visualizações totais</p>
                <p className="font-semibold text-lg">{viewsTotal}</p>
                <p className="text-xs text-muted-foreground">Toda vez que o evento é aberto</p>
              </div>
            </div>
          </div>

          {/* Statistics Section */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Estatísticas
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <Eye className="w-3.5 h-3.5 mx-auto mb-1 text-muted-foreground" />
                <p className="text-2xl font-bold">{viewsUnique}</p>
                <p className="text-xs text-muted-foreground">Únicas</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <BarChart3 className="w-3.5 h-3.5 mx-auto mb-1 text-muted-foreground" />
                <p className="text-2xl font-bold">{viewsTotal}</p>
                <p className="text-xs text-muted-foreground">Totais</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold">{event.favoriteUsers?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Favoritos</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold">{event.attendeesCount || 0}</p>
                <p className="text-xs text-muted-foreground">Confirmados</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold">{isBoostActive ? '🔥' : '-'}</p>
                <p className="text-xs text-muted-foreground">Boost</p>
              </div>
              <div className="col-span-2 md:col-span-5 bg-muted/50 rounded-lg p-3 flex items-center justify-center gap-2 text-sm">
                <Globe className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="font-medium">Site:</span>
                <span>{webViewsUnique} únicas · {webViewsTotal} totais</span>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <h3 className="text-lg font-semibold">Sobre o Evento</h3>
            <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
              {event.description}
            </p>
          </div>

          {/* Map */}
          {hasValidCoordinates && (
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">Localização no Mapa</h3>
              <div className="rounded-lg overflow-hidden border border-border h-64">
                <iframe
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  style={{ border: 0 }}
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${event.longitude-0.01},${event.latitude-0.01},${event.longitude+0.01},${event.latitude+0.01}&layer=mapnik&marker=${event.latitude},${event.longitude}`}
                  title="Mapa do evento"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Coordenadas: {event.latitude?.toFixed?.(6) || event.latitude}, {event.longitude?.toFixed?.(6) || event.longitude}
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="pt-4 border-t border-border">
            <Button onClick={onClose} className="w-full">
              Fechar Preview
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

